import { useCallback, useEffect, useRef, useState } from "react";
import { api, ingestApi } from "../../api";
import { uploadFile } from "../../org/hooks/OrgDocument/useUploadQueue";
import { parseRuntimeResult } from "../../org/utils/agentInvocationResult";
import {
  InvocationStatusEnum,
  type UserOrgRole,
  type UserOrgRoleOrgCategory,
} from "../../generated/core-api";
import { ProcessingMode, ProcessingStatus } from "../../generated/ingest-api";
import { companyShellJsonToCsv } from "./companyShellJsonToCsv";

/**
 * What a completed extraction hands back.
 *
 * The omitted columns travel with the CSV rather than being read from hook
 * state, so the caller writes both into the batch in one update. Reading them
 * from state after `await` would see the value from the render that started
 * the run.
 */
export interface DocumentExtractionResult {
  /** Upload-CSV text, ready for the same parse a `.csv` upload takes. */
  csv: string;
  /** Template columns the agent returned no value for. */
  missingColumns: string[];
}

/** Which leg of the extraction is running, for the progress UI. */
export type DocumentExtractionStage =
  | "idle"
  | "uploading"
  | "extracting"
  | "reading"
  | "done"
  | "error";

export interface DocumentExtractionState {
  stage: DocumentExtractionStage;
  /** Populated once `stage === "error"`; always user-facing prose. */
  error: string | null;
  /** Contract columns the agent omitted, surfaced so prompt drift is visible. */
  missingColumns: string[];
  isRunning: boolean;
}

export interface ExtractCompaniesArgs {
  file: File;
  agentId: string;
  serviceAccountId: string;
}

const DOCUMENT_POLL_INTERVAL_MS = 2000;
/** ~4 minutes. Markdown extraction on a long PDF is the slow leg, not the agent. */
const DOCUMENT_POLL_ATTEMPTS = 120;
const INVOCATION_POLL_INTERVAL_MS = 2000;
/** ~5 minutes. A measured run took 16s; this is headroom, not an expectation. */
const INVOCATION_POLL_ATTEMPTS = 150;

/** Last resort: only reached if this hook throws something it did not wrap. */
const GENERIC_FAILURE =
  "Could not read companies from this document. Try again, or upload a CSV instead.";

/**
 * The assistant refused us: a wrong agent or service-account id, or a service
 * account without the grants this flow needs. Retrying cannot fix either.
 */
const ASSISTANT_MISCONFIGURED =
  "The document assistant is not available. Ask an administrator to check its configuration and permissions.";

/** The assistant did not answer — down, or the network dropped. Worth a retry. */
const ASSISTANT_UNREACHABLE =
  "Could not reach the document assistant. Try again in a few minutes.";

const UPLOAD_NOT_PERMITTED =
  "You do not have permission to upload documents for the document assistant. Ask an administrator to check your access.";

const UPLOAD_FAILED =
  "Could not upload this document. Check your connection and try again.";

const PROCESSING_UNAVAILABLE =
  "Could not start reading this document. Try again in a few minutes.";

/** The run finished, but not as COMPLETED — the document itself is suspect. */
const AGENT_RUN_FAILED =
  "The document assistant could not extract companies from this document. Check it is a completed questionnaire, or upload a CSV instead.";

/** A failure whose message is already written for this screen. */
class ExtractionFailure extends Error {}

interface StepMessages {
  /**
   * Shown when the service refused the call. Omitted where refusal is not a
   * distinct outcome — a rejected SAS upload needs the same retry as a dropped
   * connection.
   */
  denied?: string;
  /** Shown when the call failed for any other reason. */
  failed: string;
}

/**
 * Whether the service refused the call rather than failing to answer it.
 *
 * 403 means the caller's grants are wrong; 404 on these endpoints means a
 * configured id does not exist. Both need an administrator, not a retry. 401
 * is absent deliberately: `addUnauthorizedInterceptor` redirects into the auth
 * flow before any message here could be read.
 */
function isAccessDenied(caught: unknown): boolean {
  if (typeof caught !== "object" || caught === null) return false;
  const { response } = caught as { response?: { status?: unknown } };
  if (typeof response !== "object" || response === null) return false;
  return response.status === 403 || response.status === 404;
}

/**
 * Run one leg of the extraction, replacing whatever it throws with prose.
 *
 * Wrapping at each call site is what lets the catch block say which step
 * failed and what to do about it. Errors from here name blob containers, SAS
 * URLs and HTTP status codes, none of which belong on screen or help the
 * person reading it.
 */
async function step<T>(
  messages: StepMessages,
  work: () => Promise<T>,
): Promise<T> {
  try {
    return await work();
  } catch (caught) {
    if (caught instanceof ExtractionFailure) throw caught;
    const message =
      messages.denied !== undefined && isAccessDenied(caught)
        ? messages.denied
        : messages.failed;
    throw new ExtractionFailure(message, { cause: caught });
  }
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * A document identity unique to this extraction, keeping the original filename
 * readable on the end.
 *
 * Every questionnaire lands in the one service-account org, and the API derives
 * `doc_id` from `doc_name` via `sanitize_filename`. Uploading the file name
 * as-is therefore makes two people uploading `Questionnaire.docx` share a
 * single catalog entry and blob: the second upload replaces the first, and
 * whichever agent run retrieves afterwards reads the other person's
 * questionnaire while the screen still shows the original filename.
 *
 * The prefix leads so the API's extension check still sees `.docx`, and uses
 * only characters `sanitize_filename` passes through — notably no `:`, which
 * is why the timestamp is compacted rather than ISO.
 */
function uniqueDocName(fileName: string): string {
  const stamp = new Date().toISOString().replace(/[-:T.]/g, "").slice(0, 14);
  return `${stamp}-${crypto.randomUUID().slice(0, 8)}-${fileName}`;
}

/**
 * Whether an org may hold an uploaded questionnaire.
 *
 * Public orgs are readable by any authenticated user, and `markdown_only`
 * suppresses indexing rather than document access — a customer's completed
 * questionnaire stored there is downloadable by unrelated tenants. Retrieval
 * *from* a public org stays supported; only using one as a destination does
 * not. Spelled out per category so that adding one fails the build and forces
 * a decision, instead of inheriting upload access by default.
 */
const CAN_HOLD_UPLOADS: Record<UserOrgRoleOrgCategory, boolean> = {
  internal: true,
  personal: true,
  public: false,
};

/**
 * The org to upload into: the service account's own org.
 *
 * Read from the service account rather than configured separately. The agent
 * can only retrieve markdown from an org its `markdown.retrieve` grant covers,
 * and that grant is scoped to this org role — a second config value could only
 * ever disagree with it, and uploading somewhere the agent cannot read fails
 * at the retrieve step with a far less obvious error.
 */
function resolveUploadOrg(orgRoles: UserOrgRole[] | undefined): UserOrgRole {
  const role = orgRoles?.[0];
  if (!role) {
    throw new ExtractionFailure(
      "The document assistant has no organisation assigned. Ask an administrator to check its configuration.",
    );
  }
  if (!CAN_HOLD_UPLOADS[role.org_category]) {
    throw new ExtractionFailure(
      "The document assistant's organisation cannot hold uploaded questionnaires. Ask an administrator to check its configuration.",
    );
  }
  return role;
}

/**
 * Orchestrates turning an uploaded questionnaire into upload CSV:
 * resolve org → upload → process (`markdown_only`) → poll the document →
 * invoke Doc2Company → poll the invocation → serialise the keyed JSON.
 *
 * Sequential `await` with explicit sleeps rather than TanStack Query: this is
 * one user-initiated command with six dependent steps, not cached server state,
 * and modelling it as chained queries would make the per-stage progress and
 * cancellation harder to follow than it is here.
 */
export function useDocumentCompanyExtraction() {
  const [stage, setStage] = useState<DocumentExtractionStage>("idle");
  const [error, setError] = useState<string | null>(null);
  const [missingColumns, setMissingColumns] = useState<string[]>([]);
  // Guards against a late resolution writing state after the user replaced the
  // file or left the screen.
  const runIdRef = useRef(0);

  // Unmounting invalidates a run in flight, the same way replacing the file
  // does. Without this the wizard provider outlives this screen, so a late
  // resolution hands CSV back to the closure captured before navigation —
  // replacing Review's rows and clearing validation against a revision taken
  // before the user left.
  useEffect(() => {
    return () => {
      runIdRef.current += 1;
    };
  }, []);

  const reset = useCallback(() => {
    runIdRef.current += 1;
    setStage("idle");
    setError(null);
    setMissingColumns([]);
  }, []);

  const extract = useCallback(
    async ({
      file,
      agentId,
      serviceAccountId,
    }: ExtractCompaniesArgs): Promise<DocumentExtractionResult | null> => {
      const runId = ++runIdRef.current;
      const isStale = () => runIdRef.current !== runId;
      const fail = (message: string): null => {
        if (!isStale()) {
          setStage("error");
          setError(message);
        }
        return null;
      };

      setStage("uploading");
      setError(null);
      setMissingColumns([]);

      try {
        const serviceAccount = await step(
          { denied: ASSISTANT_MISCONFIGURED, failed: ASSISTANT_UNREACHABLE },
          () => api.getServiceAccount(agentId, serviceAccountId),
        );
        const org = resolveUploadOrg(serviceAccount.org_roles);

        const docName = uniqueDocName(file.name);
        const { upload_url, doc_id } = await step(
          { denied: UPLOAD_NOT_PERMITTED, failed: UPLOAD_FAILED },
          () =>
            ingestApi.generateUploadUrl({
              org_id: org.org_id,
              org_category: org.org_category,
              doc_name: docName,
              doc_id: docName,
              doc_source: "upload",
              doc_tags: [],
            }),
        );
        await step({ failed: UPLOAD_FAILED }, () =>
          uploadFile(file, upload_url),
        );
        if (isStale()) return null;

        // markdown_only: the agent reads the markdown companion, and chunking,
        // embedding and indexing would add latency and index a customer
        // questionnaire for retrieval nobody asked for.
        await step({ failed: PROCESSING_UNAVAILABLE }, () =>
          ingestApi.processUploadedDocument(
            org.org_category,
            org.org_id,
            doc_id,
            { processing_mode: ProcessingMode.markdown_only },
          ),
        );
        if (isStale()) return null;

        setStage("extracting");
        const extracted = await step({ failed: PROCESSING_UNAVAILABLE }, () =>
          waitForMarkdown(org.org_category, org.org_id, doc_id, isStale),
        );
        if (isStale()) return null;
        if (extracted === "stale") return null;
        if (extracted === "failed") {
          return fail(
            "We could not read any text from this document. Check it is not a scan or an empty file.",
          );
        }
        if (extracted === "timeout") {
          return fail(
            "Reading this document is taking longer than expected. Try again in a few minutes.",
          );
        }

        setStage("reading");
        // The run's own `doc_id`, never `file.name`: the agent must retrieve
        // the copy this extraction uploaded, not another run's document of the
        // same name. Read back from the response rather than re-derived, so a
        // name the API sanitises cannot silently break retrieval.
        const started = await step(
          { denied: ASSISTANT_MISCONFIGURED, failed: ASSISTANT_UNREACHABLE },
          () =>
            api.invokeAgent(agentId, {
              service_account_id: serviceAccountId,
              input_payload: {
                workflow: "agent_runtime",
                instruction: `fetch ${doc_id} and extract data`,
              },
            }),
        );
        if (isStale()) return null;

        const finished = await step({ failed: ASSISTANT_UNREACHABLE }, () =>
          waitForInvocation(agentId, started.workflow_id, isStale),
        );
        if (isStale()) return null;
        if (finished === "stale") return null;
        if (finished === "timeout") {
          return fail(
            "The document assistant did not respond in time. Try again.",
          );
        }
        if (finished.status !== InvocationStatusEnum.COMPLETED) {
          return fail(AGENT_RUN_FAILED);
        }

        const answer = parseRuntimeResult(finished.result)?.answer ?? "";
        const serialised = companyShellJsonToCsv(answer);
        if (!serialised.ok) {
          return fail(serialised.reason);
        }

        if (!isStale()) {
          setMissingColumns(serialised.missingColumns);
          setStage("done");
        }
        return {
          csv: serialised.csv,
          missingColumns: serialised.missingColumns,
        };
      } catch (caught) {
        // Every await above is wrapped, so an ExtractionFailure already says
        // which step failed and what to do. Anything else is a defect in this
        // hook rather than a failure of a call it made.
        return fail(
          caught instanceof ExtractionFailure ? caught.message : GENERIC_FAILURE,
        );
      }
    },
    [],
  );

  return {
    extract,
    reset,
    stage,
    error,
    missingColumns,
    isRunning: stage === "uploading" || stage === "extracting" || stage === "reading",
  } satisfies DocumentExtractionState & {
    extract: (
      args: ExtractCompaniesArgs,
    ) => Promise<DocumentExtractionResult | null>;
    reset: () => void;
  };
}

type MarkdownWaitOutcome = "ready" | "failed" | "timeout" | "stale";

/**
 * Poll the org's document list until *this* upload has produced markdown.
 *
 * `doc_last_markdown_created_at` is the signal rather than
 * `processing_status === completed`: it is the thing the agent actually needs,
 * and under `markdown_only` it is stamped only after the blob write lands.
 *
 * Its mere presence is not enough. Re-uploading a document that was ingested
 * before resets `processing_status` to pending but leaves the previous run's
 * `doc_last_markdown_created_at` in place, so a presence check passes on the
 * first poll and the agent reads the companion while it is being replaced —
 * returning a fully blank row. `doc_last_catalogued_at` is re-stamped by every
 * upload, so requiring the markdown to be newer than it distinguishes this
 * run's output from the previous one. Both are written by the same service, so
 * they share a clock.
 */
async function waitForMarkdown(
  orgCategory: UserOrgRole["org_category"],
  orgId: string,
  docId: string,
  isStale: () => boolean,
): Promise<MarkdownWaitOutcome> {
  for (let attempt = 0; attempt < DOCUMENT_POLL_ATTEMPTS; attempt++) {
    await sleep(DOCUMENT_POLL_INTERVAL_MS);
    if (isStale()) return "stale";

    const documents = await ingestApi.listDocumentsByOrg(orgCategory, orgId);
    const document = documents.find((candidate) => candidate.doc_id === docId);
    if (!document) continue;
    if (document.processing_status === ProcessingStatus.failed) return "failed";
    if (isMarkdownFromThisUpload(document)) return "ready";
  }
  return "timeout";
}

/**
 * True once the document's markdown post-dates the catalogue entry this upload
 * created. A document with no markdown at all, or markdown left over from an
 * earlier ingest of the same filename, is not ready.
 */
function isMarkdownFromThisUpload(document: {
  doc_last_markdown_created_at?: string | null;
  doc_last_catalogued_at?: string | null;
}): boolean {
  const markdownAt = Date.parse(document.doc_last_markdown_created_at ?? "");
  if (Number.isNaN(markdownAt)) return false;
  const cataloguedAt = Date.parse(document.doc_last_catalogued_at ?? "");
  // No catalogue timestamp to compare against: fall back to presence rather
  // than blocking a document that is genuinely ready.
  if (Number.isNaN(cataloguedAt)) return true;
  return markdownAt >= cataloguedAt;
}

async function waitForInvocation(
  agentId: string,
  workflowId: string,
  isStale: () => boolean,
): Promise<
  | { status: string; result: unknown }
  | "timeout"
  | "stale"
> {
  for (let attempt = 0; attempt < INVOCATION_POLL_ATTEMPTS; attempt++) {
    await sleep(INVOCATION_POLL_INTERVAL_MS);
    if (isStale()) return "stale";

    const status = await api.getInvocationStatus(agentId, workflowId);
    if (
      status.status === InvocationStatusEnum.COMPLETED ||
      status.status === InvocationStatusEnum.FAILED ||
      status.status === InvocationStatusEnum.TERMINATED
    ) {
      return { status: status.status, result: status.result };
    }
  }
  return "timeout";
}
